import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/nextjs';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  LessonAgeGroupsEnum,
  SkillLevelValuesEnum,
  useUpdateCoachLessonAgeGroupsMutation,
} from 'types/generated/client';
import { LessonAgeGroupUpdate, SkillLevelGroupUpdate } from 'utils/shared/coachBuilder';
import toTitleCase from 'utils/shared/string/toTitleCase';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonText } from 'components/Button';
import ProfileItem from 'components/coaches-builder/ProfileItem';
import CheckboxType from 'components/forms/checkboxes/CheckBoxType';
import Modal from 'components/modals/Modal';
import { whoITeachSchema } from './schemas';
import { FormModalModeEnum, FormModalProps } from '../types';

interface WhoITeachFormModalProps extends FormModalProps {}

const customSkillLevelOrder = [
  SkillLevelValuesEnum.BrandNew,
  SkillLevelValuesEnum.Beginner,
  SkillLevelValuesEnum.Intermediate,
  SkillLevelValuesEnum.Advanced,
];

const customLessonGroupOrder = [
  LessonAgeGroupsEnum.Youth,
  LessonAgeGroupsEnum.Adult,
  LessonAgeGroupsEnum.Senior,
];

type WhoITeachSchemaType = z.infer<typeof whoITeachSchema>;

const WhoITeachFormModal: React.FC<WhoITeachFormModalProps> = ({
  isOpen,
  handleClose,
  mode,
  coachData,
}) => {
  const { control, reset, setValue, handleSubmit } = useForm<WhoITeachSchemaType>({
    resolver: zodResolver(whoITeachSchema),
  });

  const [updateCoachLessonAgeGroups, { loading: coachLessonAgeGroupsLoading }] =
    useUpdateCoachLessonAgeGroupsMutation();

  const viewer = useViewer();

  const onSubmit = async (data: WhoITeachSchemaType) => {
    const coachLessonAgeGroups: LessonAgeGroupUpdate[] = [];

    Object.keys(data?.ageGroups).map((ageGroup) => {
      if (data?.ageGroups[ageGroup]) {
        coachLessonAgeGroups.push({
          lessonAgeGroup: ageGroup as LessonAgeGroupsEnum,
          userId: viewer?.userId || '',
        });
      }
    });

    const coachSkillLevelGroups: SkillLevelGroupUpdate[] = [];

    Object.keys(data?.skillLevels).map((skillLevel) => {
      if (data?.skillLevels[skillLevel]) {
        coachSkillLevelGroups.push({
          skillLevel: skillLevel as SkillLevelValuesEnum,
          userId: viewer?.userId || '',
        });
      }
    });
    try {
      await updateCoachLessonAgeGroups({
        variables: {
          coachLessonAgeGroups,
          coachSkillLevelGroups,
          userId: viewer.userId,
        },
      });
      handleClose(true);
    } catch (err) {
      captureException(err);
    }
  };

  useEffect(() => {
    // if (mode === FormModalModeEnum.Add) {
    //   reset();
    //   return;
    // }
    const skillLevelsObject: { [key: string]: boolean } = {};

    coachData?.coachLessonSkillLevels?.forEach((coachLessonSkillLevel) => {
      skillLevelsObject[coachLessonSkillLevel.skillLevel] = true;
    });

    const ageGroupObject: { [key: string]: boolean } = {};

    coachData?.coachLessonAgeGroups?.forEach((coachLessonAgeGroup) => {
      ageGroupObject[coachLessonAgeGroup.lessonAgeGroup] = true;
    });

    if (Object.keys(ageGroupObject)?.length) setValue('ageGroups', ageGroupObject);

    if (Object.keys(skillLevelsObject)?.length) setValue('skillLevels', skillLevelsObject);
  }, [coachData, reset, setValue, mode]);

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} classNameMaxWidth="max-w-[80vw]">
      <div className="flex items-center justify-between border-b px-8 py-4">
        <h2 className="typography-product-heading-desktop">Who I teach</h2>
        <ButtonText onClick={() => handleClose()}>
          <CloseIcon className="h-4 w-4" />
        </ButtonText>
      </div>
      <div className="flex flex-col px-4 md:px-10">
        <ProfileItem
          label="What age groups do you teach?"
          description="Let players know which age groups you prefer teaching."
          component={
            <div>
              {customLessonGroupOrder.map((group) => (
                <CheckboxType
                  key={group}
                  name={`ageGroups.${group}`}
                  label={toTitleCase(group)}
                  control={control}
                  checkBoxClass="h-4 w-4"
                  textClass=""
                />
              ))}
            </div>
          }
        />
        <ProfileItem
          label="What skill levels do you teach?"
          description="Let players know which skill set you prefer teaching."
          component={
            <div>
              {Object.entries(customSkillLevelOrder).map(([levelKey, levelValue]) => {
                if (['BRAND_NEW', 'BEGINNER', 'INTERMEDIATE', 'ADVANCED'].includes(levelValue)) {
                  return (
                    <CheckboxType
                      key={levelKey}
                      name={`skillLevels.${levelValue}`}
                      label={toTitleCase(levelValue)}
                      control={control}
                      checkBoxClass="h-4 w-4"
                      textClass=""
                    />
                  );
                }
              })}
            </div>
          }
        />
      </div>
      <div className=" flex justify-end border-t px-8 py-4">
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={coachLessonAgeGroupsLoading}
          className="w-fit"
          size="lg"
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default WhoITeachFormModal;
